import moment from 'moment'
import { getShiftIdentityKey } from '../../../../helpers/shiftIdentity'
import { toTravelDraft } from '../../../../helpers/shiftTravel'

// Must match ON_CALL_TEAMS in server/src/helpers/scheduling/shiftAssignmentRules.js
export const ON_CALL_TEAMS = ['Manager', 'Supervisor']

export const isOnCallShift = (shift) =>
  !!(shift?.isOnCall || shift?.roster?.isOnCall)

export const isDraftRosterShift = (shift) =>
  !!shift?.roster && shift.roster?.status === 'Draft'

const isVacantShift = (shift) =>
  !shift?.assignedTo && !shift?.assignedSubcontractor && !shift?.assignedToSubcontractor

// A day cell with 4 identical vacant Paramedic slots is one advertised
// vacancy, not 4 things for a manager to scan past - collapse same-identity
// vacant shifts into a single card carrying a count. Assigned shifts are
// always shown individually (a person is never interchangeable).
export const collapseIdenticalVacantShifts = (shifts = []) => {
  const seenAt = new Map()
  const collapsed = []

  shifts.forEach((shift) => {
    if (!isVacantShift(shift)) {
      collapsed.push({ ...shift, duplicateCount: 1 })
      return
    }

    const key = getShiftIdentityKey(shift)
    const existingIndex = seenAt.get(key)

    if (existingIndex === undefined) {
      seenAt.set(key, collapsed.length)
      collapsed.push({ ...shift, duplicateCount: 1 })
    } else {
      collapsed[existingIndex].duplicateCount += 1
    }
  })

  return collapsed
}

// Packs date-spanning entries ({ item, start, end }) into horizontal spans
// across the visible day columns (rather than one copy per day). Entries
// that share a day are stacked into "lanes" so spans never overlap. Returns
// each entry with its day-column range, lane index, and whether it continues
// beyond the visible date range in either direction. Shared by the shift
// cards and the availability bands.
const layoutSpans = (entries = [], days = []) => {
  if (!days.length || !entries.length) return { laidOut: [], laneCount: 0 }

  const rangeStart = days[0].clone().startOf('day')
  const rangeEnd = days[days.length - 1].clone().endOf('day')

  const items = entries
    .map(({ item, start, end }) => {
      const startIdx = Math.min(
        Math.max(moment(start).startOf('day').diff(rangeStart, 'days'), 0),
        days.length - 1,
      )
      const endIdx = Math.min(
        Math.max(moment(end).startOf('day').diff(rangeStart, 'days'), 0),
        days.length - 1,
      )
      return {
        item,
        startIdx,
        endIdx,
        continuesFromPrior: moment(start).isBefore(rangeStart),
        continuesToNext: moment(end).isAfter(rangeEnd),
      }
    })
    .sort(
      (a, b) =>
        a.startIdx - b.startIdx ||
        new Date(a.item.start) - new Date(b.item.start),
    )

  // Greedy lane assignment on day granularity - first lane whose last
  // span ends before this one starts
  const laneLastEnd = []
  const laidOut = items.map((item) => {
    let lane = laneLastEnd.findIndex((lastEnd) => lastEnd < item.startIdx)
    if (lane === -1) {
      lane = laneLastEnd.length
      laneLastEnd.push(item.endIdx)
    } else {
      laneLastEnd[lane] = item.endIdx
    }
    return { ...item, lane }
  })

  return { laidOut, laneCount: laneLastEnd.length }
}

export const layoutRowShifts = (shifts = [], days = []) => {
  const { laidOut, laneCount } = layoutSpans(
    collapseIdenticalVacantShifts(shifts).map((shift) => ({
      item: shift,
      start: shift.start,
      end: shift.end,
    })),
    days,
  )
  return {
    laidOutShifts: laidOut.map(({ item, ...span }) => ({ shift: item, ...span })),
    laneCount: Math.max(laneCount, 1),
  }
}

// Anything else with a start/end (e.g. crew-less PTS duties) laid out as
// spans in a row of its own
export const layoutRowSpans = (items = [], days = []) => {
  const { laidOut, laneCount } = layoutSpans(
    items.map((item) => ({ item, start: item.start, end: item.end })),
    days,
  )
  return { laidOutItems: laidOut, laneCount: Math.max(laneCount, 1) }
}

// Availability bands sit in their own lanes above a staff row's shifts.
// laneCount is 0 when the employee has nothing declared, so rows without
// availability keep their current height.
export const layoutRowAvailability = (records = [], days = []) => {
  const { laidOut, laneCount } = layoutSpans(
    records.map((record) => ({
      item: record,
      start: record.start,
      end: record.end,
    })),
    days,
  )
  return {
    laidOutBands: laidOut.map(({ item, ...span }) => ({ record: item, ...span })),
    laneCount,
  }
}

// Staff availability (StaffAvailability records) on the week grid: a green
// band for a declared available window, hatched red/amber for approved /
// pending leave and unavailable periods.
export const availabilityBandStyles = {
  available: {
    id: 'available',
    label: 'Available to work',
    bg: '#ECFDF5',
    fg: '#047857',
    border: '#6EE7B7',
  },
  unavailable: {
    id: 'unavailable',
    label: 'Leave / unavailable',
    bg: '#FEF2F2',
    fg: '#B91C1C',
    border: '#FCA5A5',
    hatched: true,
  },
  pendingUnavailable: {
    id: 'pendingUnavailable',
    label: 'Leave request pending',
    bg: '#FFFBEB',
    fg: '#92400E',
    border: '#FCD34D',
    hatched: true,
  },
}

export const isAvailableRecord = (record) =>
  record?.type === 'Available' && record?.status === 'Approved'

export const getAvailabilityBandStyle = (record) => {
  if (record?.type === 'Available') return availabilityBandStyles.available
  if (record?.status === 'Pending') return availabilityBandStyles.pendingUnavailable
  return availabilityBandStyles.unavailable
}

export const rowHasAvailableWindow = (row) =>
  (row?.availability ?? []).some(isAvailableRecord)

// Distinct staff who can work on each day of a range: an Approved 'Available'
// window touching the day, not overridden by approved leave / sickness /
// unavailable on that same day (unavailability wins, as on the server).
// Keyed YYYY-MM-DD → { count, names } - feeds the year-view day borders, the
// month-view footers and the week-view "Available staff" row.
export const buildAvailableStaffByDay = (records = [], rangeStart, rangeEnd) => {
  const first = moment(rangeStart).startOf('day')
  const last = moment(rangeEnd).startOf('day')
  const eachDay = (record, visit) => {
    const from = moment.max(moment(record.start).startOf('day'), first)
    const to = moment.min(moment(record.end).startOf('day'), last)
    for (const day = from.clone(); day.isSameOrBefore(to, 'day'); day.add(1, 'day')) {
      visit(day.format('YYYY-MM-DD'))
    }
  }

  const blockedByDay = {}
  records.forEach((record) => {
    if (record?.status !== 'Approved' || record?.type === 'Available') return
    const employeeID = String(getEntityId(record.employee) ?? '')
    eachDay(record, (key) => {
      if (!blockedByDay[key]) blockedByDay[key] = new Set()
      blockedByDay[key].add(employeeID)
    })
  })

  const byDay = {}
  const seen = {}
  records.forEach((record) => {
    if (!isAvailableRecord(record)) return
    const employeeID = String(getEntityId(record.employee) ?? '')
    eachDay(record, (key) => {
      if (blockedByDay[key]?.has(employeeID)) return
      if (!seen[key]) seen[key] = new Set()
      if (seen[key].has(employeeID)) return
      seen[key].add(employeeID)
      if (!byDay[key]) byDay[key] = { count: 0, names: [] }
      byDay[key].count += 1
      byDay[key].names.push(getEmployeeName(record.employee) || 'Staff member')
    })
  })
  return byDay
}

export const describeAvailableStaff = (entry) => {
  if (!entry?.count) return 'No staff have said they are available'
  const shown = entry.names.slice(0, 8)
  const more = entry.names.length - shown.length
  return `Available: ${shown.join(', ')}${more > 0 ? ` and ${more} more` : ''}`
}

// Year view "Source" display: colour each day by where its shifts come from
export const shiftSourceGroups = {
  Event: { label: 'Events', color: '#3B82F6' },
  PTS: { label: 'PTS', color: '#0D9488' },
  'Rota Plan': { label: 'Rota plan', color: '#8B5CF6' },
  'On Call': { label: 'On call', color: '#F59E0B' },
  Frontline: { label: 'Frontline', color: '#0EA5E9' },
  Mixed: { label: 'Mixed sources', color: '#64748B' },
}

export const getShiftSourceGroup = (shift) => {
  if (shift?.event || shift?.shiftType === 'Event') return 'Event'
  if (shift?.ptsDuty || shift?.shiftType === 'PTS') return 'PTS'
  if (shift?.roster) return isOnCallShift(shift) ? 'On Call' : 'Rota Plan'
  if (shift?.frontlineDuty || shift?.shiftType === 'FrontLine') return 'Frontline'
  return 'Rota Plan'
}

export const getYearDaySource = (summary) => {
  const groups = Object.keys(summary?.sources ?? {}).filter(
    (group) => summary.sources[group] > 0,
  )
  if (!groups.length) return null
  return groups.length === 1 ? groups[0] : 'Mixed'
}

export const onCallLegendItem = {
  id: 'oncall',
  label: 'On call (dashed)',
  bg: '#FFFFFF',
  fg: '#334155',
  border: '#64748B',
  chipBg: '#FFFFFF',
  dashed: true,
}

// A PTS duty with no crew shifts yet - shown under Vacant Shifts as work
// nobody has been put on
export const uncrewedDutyLegendItem = {
  id: 'uncrewedDuty',
  label: 'PTS duty · no crew yet',
  bg: '#FEF2F2',
  fg: '#B91C1C',
  border: '#F87171',
  chipBg: '#FFFFFF',
  dashed: true,
}

export const draftRosterLegendItem = {
  id: 'draftRoster',
  label: 'Draft rota plan',
  bg: '#239AA4',
  fg: '#FFFFFF',
  border: '#1E7F88',
  chipBg: 'rgba(255, 255, 255, 0.14)',
  pulse: true,
}

export const rosterStatusStyles = {
  swap: {
    label: 'Swap request',
    bg: '#85276A',
    fg: '#FFFFFF',
    border: '#6A1F55',
    chipBg: 'rgba(255, 255, 255, 0.14)',
  },
  application: {
    label: 'Application',
    bg: '#FFF7ED',
    fg: '#9A3412',
    border: '#FDBA74',
    chipBg: '#FFFFFF',
  },
  uncovered: {
    label: 'Uncovered',
    bg: '#FEF2F2',
    fg: '#B91C1C',
    border: '#FCA5A5',
    chipBg: '#FFFFFF',
  },
  notAcknowledged: {
    label: 'Not acknowledged',
    bg: '#FFFBEB',
    fg: '#92400E',
    border: '#FCD34D',
    chipBg: '#FFFFFF',
  },
  acknowledged: {
    label: 'Acknowledged',
    bg: '#ECFDF5',
    fg: '#047857',
    border: '#A7F3D0',
    chipBg: '#FFFFFF',
  },
  covered: {
    label: 'Covered',
    bg: '#F8FAFC',
    fg: '#334155',
    border: '#CBD5E1',
    chipBg: '#FFFFFF',
  },
}

export const rosterStatusLegend = [
  { id: 'swap', ...rosterStatusStyles.swap },
  { id: 'application', ...rosterStatusStyles.application },
  { id: 'uncovered', ...rosterStatusStyles.uncovered },
  { id: 'notAcknowledged', ...rosterStatusStyles.notAcknowledged },
  { id: 'acknowledged', ...rosterStatusStyles.acknowledged },
  { id: 'covered', ...rosterStatusStyles.covered },
]

export const getShiftStatus = (shift, pendingApplications = []) => {
  if (shift.availableForReassignment) return 'swap'
  if (pendingApplications.length > 0) return 'application'
  if (
    !shift.assignedTo &&
    !shift.assignedSubcontractor &&
    !shift.assignedToSubcontractor
  ) {
    return 'uncovered'
  }

  // On-call shifts never require acknowledgement (guard covers legacy
  // on-call shifts stamped required before the exemption)
  if (shift?.acknowledgement?.required && !isOnCallShift(shift)) {
    return shift?.acknowledgement?.acknowledgedAt
      ? 'acknowledged'
      : 'notAcknowledged'
  }

  return 'covered'
}

export const getShiftCardStyle = (shift, pendingApplications = []) =>
  isDraftRosterShift(shift)
    ? draftRosterLegendItem
    : (rosterStatusStyles[getShiftStatus(shift, pendingApplications)] ??
      rosterStatusStyles.covered)

export const getVisibleRosterStatusLegend = (
  groupedRows = [],
  getShiftApplications = () => [],
) => {
  const visibleStatuses = new Set()
  const visibleBands = new Set()
  let hasOnCall = false
  let hasDraftRoster = false

  groupedRows.forEach((row) => {
    row.shifts.forEach((shift) => {
      visibleStatuses.add(getShiftStatus(shift, getShiftApplications(shift)))
      if (isDraftRosterShift(shift)) hasDraftRoster = true
      if (isOnCallShift(shift)) hasOnCall = true
    })
    ;(row.availability ?? []).forEach((record) => {
      visibleBands.add(getAvailabilityBandStyle(record).id)
    })
  })

  return [
    ...(hasDraftRoster ? [draftRosterLegendItem] : []),
    ...rosterStatusLegend.filter((item) => visibleStatuses.has(item.id)),
    ...(hasOnCall ? [onCallLegendItem] : []),
    ...Object.values(availabilityBandStyles).filter((item) =>
      visibleBands.has(item.id),
    ),
  ]
}

export const PTS_SHARED_ORGANISATION_LABEL = 'Multiple organisations'

// Source display: a PTS shift belongs to the organisation its duty is
// dedicated to (duty.organisation); duties that carry work for anyone share
// one "Multiple organisations" row. Empty for non-PTS shifts.
export const getPtsOrganisationLabel = (shift) => {
  if (!(shift?.ptsDuty || shift?.shiftType === 'PTS')) return ''
  return shift?.ptsDuty?.organisation?.orgName || PTS_SHARED_ORGANISATION_LABEL
}

// One row per event, one per PTS organisation
export const getShiftSourceLabel = (shift) => {
  const source = getShiftSource(shift)
  const organisation = getPtsOrganisationLabel(shift)
  return organisation ? `${source} · ${organisation}` : source
}

// Month/year views have no staff rows, so each shift item carries the label
// the Display mode would otherwise have put on its row.
export const getShiftItemLabel = (shift, displayMode = 'staff') => {
  if (displayMode === 'role') return shift.clinicalRole || 'Unspecified'
  if (displayMode === 'source') return getShiftSourceLabel(shift)
  if (displayMode === 'roster') return shift.roster?.title || getShiftSource(shift)
  if (shift.assignedTo) return getEmployeeName(shift.assignedTo)
  if (shift.assignedSubcontractor) {
    return getSubcontractorName(shift.assignedSubcontractor) || 'Subcontractor'
  }
  if (shift.assignedToSubcontractor) return 'Subcontractor'
  return `Vacant · ${shift.clinicalRole || 'Unspecified'}`
}

const shiftTouchesDay = (shift, day) => {
  const dayStart = day.clone().startOf('day')
  const dayEnd = day.clone().endOf('day')
  return (
    moment(shift.start).isSameOrBefore(dayEnd) &&
    moment(shift.end).isSameOrAfter(dayStart)
  )
}

// Month calendar cells: every shift touching the day (overnight shifts show
// on both days), identical vacancies collapsed, earliest start first.
export const getShiftsForDay = (shifts = [], day) =>
  collapseIdenticalVacantShifts(shifts.filter((shift) => shiftTouchesDay(shift, day)))
    .sort((a, b) => new Date(a.start) - new Date(b.start))

// Worst-first: a day with one uncovered shift is red however many are
// covered. Colours are the mid-tone legend borders rather than the pale
// card backgrounds so small year-view squares still read at a glance.
export const yearDayStatusOrder = [
  'uncovered',
  'application',
  'swap',
  'notAcknowledged',
  'draftRoster',
  'acknowledged',
  'covered',
]

export const yearDayStatusStyles = {
  uncovered: { label: 'Uncovered', color: '#F87171' },
  application: { label: 'Applications', color: '#FB923C' },
  swap: { label: 'Swap request', color: '#A855A0' },
  notAcknowledged: { label: 'Not acknowledged', color: '#FBBF24' },
  draftRoster: { label: 'Draft rota plan', color: '#239AA4' },
  acknowledged: { label: 'Acknowledged', color: '#34D399' },
  covered: { label: 'Covered', color: '#94A3B8' },
}

export const yearDayStatusLegend = yearDayStatusOrder.map((id) => ({
  id,
  ...yearDayStatusStyles[id],
}))

export const getYearDayStatus = (summary) =>
  summary ? yearDayStatusOrder.find((status) => summary.counts[status] > 0) ?? null : null

// One entry per day of the year view's range (a calendar year or the rolling
// next-12-months window) that has at least one shift touching it:
// { total, counts: { uncovered, application, ... }, sources: { Event, PTS,
// ... } }, keyed YYYY-MM-DD. Multi-day shifts count on every day they touch
// (clamped to the range).
export const buildYearDaySummaries = (
  shifts = [],
  { start, end },
  getShiftApplications = () => [],
) => {
  const yearStart = moment(start).startOf('day')
  const yearEnd = moment(end).endOf('day')
  const summaries = {}

  shifts.forEach((shift) => {
    const status = isDraftRosterShift(shift)
      ? 'draftRoster'
      : getShiftStatus(shift, getShiftApplications(shift))
    const source = getShiftSourceGroup(shift)
    const first = moment.max(moment(shift.start).startOf('day'), yearStart)
    const last = moment.min(moment(shift.end).startOf('day'), yearEnd)

    for (
      const day = first.clone();
      day.isSameOrBefore(last, 'day');
      day.add(1, 'day')
    ) {
      const key = day.format('YYYY-MM-DD')
      if (!summaries[key]) {
        summaries[key] = { total: 0, counts: {}, sources: {} }
      }
      summaries[key].total += 1
      summaries[key].counts[status] = (summaries[key].counts[status] ?? 0) + 1
      summaries[key].sources[source] = (summaries[key].sources[source] ?? 0) + 1
    }
  })

  return summaries
}

export const getRosterLabel = (roster) => {
  if (!roster) return ''
  return `${roster.title}${roster.isOnCall ? ' • On Call' : ''}`
}

export const getBaseLocationLabel = (baseLocation) =>
  baseLocation?.name || baseLocation?.locationName || ''

export const getShiftSource = (shift) => {
  if (shift.event) return shift?.event?.title ||'Event'
  if (shift.roster) return isOnCallShift(shift) ? 'On Call' : 'Rota Plan'
  if (shift.ptsDuty) return 'PTS'
  if (shift.frontlineDuty) return 'Frontline'
  return shift.shiftType || 'Shift'
}

export const getEntityId = (entity) =>
  typeof entity === 'string' ? entity : entity?._id

export const getEmployeeName = (employee) =>
  `${employee?.userAccount?.firstName ?? ''} ${employee?.userAccount?.lastName ?? ''}`.trim()

export const getSubcontractorName = (subcontractor) =>
  `${subcontractor?.userAccount?.firstName ?? ''} ${subcontractor?.userAccount?.lastName ?? ''}`.trim()

export const getCoverageColor = (percent) => {
  if (percent >= 100) return 'success.main'
  if (percent >= 90) return 'warning.main'
  return 'error.main'
}

export const getCoverageItems = (shifts = []) => {
  const buildItem = (label, rows) => {
    const total = rows.length
    const covered = rows.filter(
      (shift) =>
        !!shift.assignedTo ||
        !!shift.assignedSubcontractor ||
        !!shift.assignedToSubcontractor,
    ).length
    const percent = total ? Math.round((covered / total) * 100) : 100

    return {
      label,
      total,
      covered,
      percent,
    }
  }

  return [
    buildItem('All', shifts),
    buildItem(
      'Events',
      shifts.filter((shift) => shift.event || shift.shiftType === 'Event'),
    ),
    buildItem(
      'PTS',
      shifts.filter((shift) => shift.ptsDuty || shift.shiftType === 'PTS'),
    ),
    buildItem(
      'Rota Plan',
      shifts.filter(
        (shift) =>
          (shift.roster || shift.shiftType === 'Admin') && !isOnCallShift(shift),
      ),
    ),
    buildItem('On Call', shifts.filter(isOnCallShift)),
  ]
}

export const createShiftDraft = ({
  day,
  roster,
  orgSettings,
  assignedTo = null,
}) => {
  const isOnCall = !!roster?.isOnCall
  const start = moment(day).hour(isOnCall ? 18 : 9).minute(0).second(0)
  const role = assignedTo?.clinicalRole || orgSettings?.roles?.[0] || ''

  return {
    roster: roster?._id ?? '',
    start,
    end: start.clone().add(isOnCall ? 14 : 8, 'hours'),
    clinicalRole: role,
    assignedTo,
    assignedSubcontractor: null,
    assignToSubcontractor: false,
    assignedToSubcontractor: false,
    shiftNote: '',
    // On-call cover is allocated, not self-booked, and usually a flat standby rate
    staffCanBook: !isOnCall,
    hourlyRate: !isOnCall,
    payRate: orgSettings?.defaultPayRates?.[role] ?? '',
    bonuses: [],
    travel: null,
    repeat: {
      enabled: false,
      daysOfWeek: [],
      until: null,
      assignAssigneeToAll: false,
    },
  }
}

export const toShiftDraft = (shift) => ({
  existingShift: shift?._id,
  roster: shift?.roster?._id ?? shift?.roster ?? '',
  start: moment(shift.start),
  end: moment(shift.end),
  clinicalRole: shift.clinicalRole ?? '',
  assignedTo: shift.assignedSubcontractor
    ? {
        _id: shift.assignedSubcontractor?.userAccount?._id,
        firstName: shift.assignedSubcontractor?.userAccount?.firstName,
        lastName: shift.assignedSubcontractor?.userAccount?.lastName,
        photograph: shift.assignedSubcontractor?.userAccount?.photograph,
        subcontractor: shift.assignedSubcontractor,
        isSubcontractor: true,
      }
    : shift.assignedTo
    ? {
        _id: shift.assignedTo?.userAccount?._id ?? shift.assignedTo?._id,
        firstName: shift.assignedTo?.userAccount?.firstName,
        lastName: shift.assignedTo?.userAccount?.lastName,
        photograph: shift.assignedTo?.userAccount?.photograph,
        employee: shift.assignedTo,
      }
    : null,
  assignedSubcontractor: null,
  assignToSubcontractor: false,
  assignedToSubcontractor: !!shift.assignedToSubcontractor,
  shiftNote: shift.shiftNote ?? '',
  staffCanBook: shift.staffCanBook ?? true,
  hourlyRate: shift.hourlyRate ?? true,
  payRate: shift.payRate ?? '',
  bonuses: (shift.bonuses ?? []).map((bonus) => ({
    label: bonus.label ?? '',
    amount: bonus.amount ?? '',
  })),
  travel: toTravelDraft(shift.travel),
  repeat: {
    enabled: false,
    daysOfWeek: [],
    until: null,
    assignAssigneeToAll: false,
  },
})
